import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { platformThemeSchema, type PlatformTheme } from '@up2d8/contracts';
import { useSession } from './session';
import { clearTenantTheme, setPlatformTheme } from './theme';

/**
 * Het platformmerk vóór het aanmelden (MRK-2, ADR-0084).
 *
 * Het aanmeldscherm, de platform-login en de wachtwoordschermen hebben geen sessie en geen
 * bedrijfscode. Zonder dit flitsen ze in de stijl van UP2D8 en springen ze om zodra iemand iets
 * intypt - of ze blijven UP2D8, bij wie het product onder een andere naam verkoopt.
 *
 * Drie stappen, van snel naar juist:
 *
 * 1. `bootPlatformBrand()` in `main.tsx`, vóór de eerste render: de laatst bekende versie uit
 *    `localStorage`. Een eerste beeld zonder verzoek.
 * 2. `usePlatformBrand()` in de providers: `GET /api/v1/platform/brand/public`, een uur gecachet.
 * 3. Zodra er een bedrijf is (de sessie, of een bedrijfscode op het aanmeldscherm), wint dat
 *    (`applyTenantTheme`). Bij afmelden komt het platformmerk terug (`clearTenantTheme`).
 */

const STORAGE_KEY = 'up2d8.platformBrand';
const QUERY_KEY = ['platform-brand-public'] as const;

function readStored(): PlatformTheme | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = platformThemeSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    // Privémodus, geblokkeerde opslag of een kapotte waarde: dan wachten we op de server.
    return null;
  }
}

function store(theme: PlatformTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // Niets aan te doen; volgende keer komt het eerste beeld dan van de server.
  }
}

/**
 * Het laatst bekende platformmerk meteen toepassen, nog voor React start.
 *
 * Een ondertekende URL (logo, achtergrond) kan intussen verlopen zijn; de schil valt dan terug op
 * de tekst (`onError` op de afbeelding) tot de verse versie er is.
 */
export function bootPlatformBrand(): void {
  const stored = readStored();
  if (stored) setPlatformTheme(stored);
}

/**
 * Haalt het platformmerk op en houdt het actueel; één keer gemonteerd, in de providers.
 *
 * Ruimt bij afmelden ook het bedrijf op: het thema van het vorige bedrijf en zijn query, zodat een
 * volgende aanmelding op een gedeeld toestel niet even in de kleuren van de vorige klant staat.
 */
export function usePlatformBrand(): void {
  const { api, profile } = useSession();
  const queryClient = useQueryClient();

  const brand = useQuery({
    queryKey: QUERY_KEY,
    // Een merk wijzigt een paar keer per jaar; elk uur opnieuw vragen is ruim genoeg.
    staleTime: 60 * 60 * 1000,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<PlatformTheme | null> => {
      const { data, error } = await api.GET('/api/v1/platform/brand/public');
      if (error || !data) return null;
      const parsed = platformThemeSchema.safeParse(data);
      return parsed.success ? parsed.data : null;
    },
  });

  useEffect(() => {
    if (!brand.data) return;
    store(brand.data);
    setPlatformTheme(brand.data);
  }, [brand.data]);

  // Van aangemeld naar afgemeld: het bedrijf eraf, het platformmerk terug.
  const wasSignedIn = useRef(false);
  useEffect(() => {
    if (profile) {
      wasSignedIn.current = true;
      return;
    }
    if (!wasSignedIn.current) return;
    wasSignedIn.current = false;
    queryClient.removeQueries({ queryKey: ['tenant-theme'] });
    clearTenantTheme();
  }, [profile, queryClient]);
}
